import { useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import LocationService, {
  AddressValidationResult,
  Location,
} from "@/app/_services/LocationService";
import { locationSchema } from "./LocationSchema";
import { toast } from "@/app/_utils/toast";

const ADDRESS_FIELDS = ["address_line_1", "address_line_2", "city", "state", "zip"] as const;

type FormData = yup.InferType<typeof locationSchema>;
type Step = "form" | "confirm";

function addressUnchanged(defaultLocation: Location | null | undefined, fields: FieldValues) {
  if (!defaultLocation) return false;
  return ADDRESS_FIELDS.every(
    (field) => (fields[field] ?? "") === (defaultLocation[field] ?? "")
  );
}

export function useLocationFormFlow({
  defaultLocation,
  onSaved,
}: {
  defaultLocation?: Location | null;
  onSaved: () => void;
}) {
  const [step, setStep] = useState<Step>("form");
  const [geocodeResult, setGeocodeResult] = useState<AddressValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingFields, setPendingFields] = useState<FieldValues | null>(null);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(locationSchema),
    defaultValues: {
      name: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      zip: "",
    },
    // `values` (unlike `defaultValues`) reactively resets the form whenever
    // defaultLocation resolves after the dialog has already mounted (e.g.
    // it loads asynchronously on a detail page opened directly by URL).
    values: defaultLocation
      ? {
          name: defaultLocation.name,
          address_line_1: defaultLocation.address_line_1,
          address_line_2: defaultLocation.address_line_2 ?? "",
          city: defaultLocation.city,
          state: defaultLocation.state,
          zip: defaultLocation.zip,
        }
      : undefined,
  });

  async function saveLocation(fields: FieldValues, geocode: AddressValidationResult | null) {
    setIsSaving(true);
    const payload = {
      ...fields,
      latitude: geocode?.latitude ?? null,
      longitude: geocode?.longitude ?? null,
      formatted_address: geocode?.formatted_address ?? null,
    };
    try {
      if (defaultLocation) {
        await LocationService.updateLocationAsync(defaultLocation.id, payload);
        toast.success("Location updated");
      } else {
        await LocationService.createLocationAsync(payload);
        toast.success("Location created");
      }
      onSaved();
    } catch (e) {
      toast.error("Failed to save location");
    } finally {
      setIsSaving(false);
    }
  }

  const handleContinue = handleSubmit(async (fields) => {
    if (addressUnchanged(defaultLocation, fields)) {
      await saveLocation(fields, {
        latitude: defaultLocation?.latitude ?? null,
        longitude: defaultLocation?.longitude ?? null,
        formatted_address: defaultLocation?.formatted_address ?? null,
        existing_location: null,
      });
      return;
    }

    setIsValidating(true);
    try {
      const result = await LocationService.validateAddressAsync({
        address_line_1: fields.address_line_1,
        address_line_2: fields.address_line_2,
        city: fields.city,
        state: fields.state,
        zip: fields.zip,
        exclude_id: defaultLocation?.id,
      });
      setGeocodeResult(result);
      setPendingFields(fields);
      setStep("confirm");
    } catch (e) {
      toast.error("Failed to validate address");
    } finally {
      setIsValidating(false);
    }
  });

  async function handleConfirm() {
    if (!pendingFields) return;
    await saveLocation(pendingFields, geocodeResult);
  }

  async function handleSaveAnyway() {
    if (!pendingFields) return;
    await saveLocation(pendingFields, null);
  }

  function handleBack() {
    setStep("form");
  }

  return {
    control,
    errors,
    getValues,
    step,
    geocodeResult,
    isValidating,
    isSaving,
    handleContinue,
    handleConfirm,
    handleSaveAnyway,
    handleBack,
  };
}
